import { createFileRoute } from "@tanstack/react-router";
import { CheckIcon, SirenIcon } from "lucide-react";
import { ALERT_KIND_LABELS } from "@brewbase/lib";
import { FermentationAlertStateEnum } from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import FarmlessIcon from "@/components/icons/farmless-icon";

export const Route = createFileRoute("/alert/$token")({
  component: AlertAckPage,
});

/**
 * The page behind the link in an alert text message.
 *
 * Opened on a phone, at night, by someone who is not signed in — so it does not
 * ask them to sign in. The token in the URL is the credential: unguessable,
 * expiring, and good for exactly one alert and one person. Everything on screen
 * is what that person needs to decide whether to get out of bed.
 */
function AlertAckPage() {
  const { token } = Route.useParams();
  const utils = trpc.useUtils();

  const { data: alert, isLoading, error } =
    trpc.fermentationAlerts.byToken.useQuery(token, { retry: false });

  const acknowledge = trpc.fermentationAlerts.acknowledgeByToken.useMutation({
    onSuccess: () => utils.fermentationAlerts.byToken.invalidate(token),
  });

  if (isLoading) {
    return <Shell>Loading…</Shell>;
  }

  if (error || !alert) {
    return (
      <Shell>
        <h1 className="text-base font-semibold">Link not valid</h1>
        <p className="text-sm text-muted-foreground">
          {error?.message ??
            "This acknowledgement link has expired or does not exist."}
        </p>
      </Shell>
    );
  }

  const acknowledged = alert.state !== FermentationAlertStateEnum.RAISED;
  const cleared = alert.state === FermentationAlertStateEnum.CLEARED;

  return (
    <Shell>
      <div className="flex items-center gap-2 text-tomato-11">
        <SirenIcon className="w-5 h-5" />
        <h1 className="text-base font-semibold">
          {ALERT_KIND_LABELS[alert.kind]}
        </h1>
      </div>

      <dl className="space-y-2">
        <Field label="Run">{alert.group_name}</Field>
        {alert.fermentation_name && (
          <Field label="Tank">{alert.fermentation_name}</Field>
        )}
        <Field label="What happened">{alert.detail}</Field>
        <Field label="Raised">
          {new Date(alert.raised_at).toLocaleString()}
        </Field>
      </dl>

      {cleared ? (
        <p className="text-sm text-muted-foreground">
          This alert has already cleared. Nothing is needed from you.
        </p>
      ) : acknowledged ? (
        <p className="flex items-center gap-2 text-sm">
          <CheckIcon className="w-4 h-4 text-green-700" />
          Acknowledged
          {alert.acked_by_name ? ` by ${alert.acked_by_name}` : ""}. Nobody else
          will be called about it.
        </p>
      ) : (
        <div className="space-y-2">
          <Button
            className="w-full"
            disabled={acknowledge.isPending}
            onClick={() => acknowledge.mutate(token)}
          >
            {acknowledge.isPending
              ? "Acknowledging…"
              : `Acknowledge as ${alert.operator_name ?? "me"}`}
          </Button>
          <p className="text-xs text-muted-foreground">
            Acknowledging stops the follow-up call. It does not close the alert
            — that happens when the process recovers, or when someone closes it
            in BrewBase.
          </p>
        </div>
      )}

      {acknowledge.error && (
        <p className="text-sm text-tomato-11">{acknowledge.error.message}</p>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-[100dvh] flex items-start justify-center bg-olive-1 py-10">
      <div className="w-full max-w-md px-6 space-y-6">
        <FarmlessIcon className="h-6 w-6" />
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </dt>
      <dd className="text-base">{children}</dd>
    </div>
  );
}
